import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import SupportModal from '@pages/Home/SupportSection/components/SupportModal';
import { Icon, Loader, Typography } from '@ui';
import { dayjs } from '@utils';
import { Flex, Spin } from 'antd';
import { observer } from 'mobx-react';
import { METHODS } from '../../constants';
import {
    StyledBadge,
    StyledBadgeError,
    StyledBadgeNoColor,
    StyledBanner,
    StyledButton,
    StyledIconWrapper
} from './styled';

const SyncBanner = observer(({ method = null }) => {
    const navigate = useNavigate();
    const store = useStore();
    const { currentAccount } = store.accountsStore;
    const { meta } = store.productsStore;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const productSetupSettings = currentAccount?.productSetupSettings;
    const { csv, googleFeed, metaFeed } = productSetupSettings || {};

    if (!csv && !googleFeed && !metaFeed) {
        return null;
    }

    const syncMethod = currentAccount?.getSyncMethod || '.CSV';

    const productCount = meta?.total;
    const isLoading = productCount === undefined || productCount === null;
    const formattedCount = isLoading ? '' : (productCount || 0).toLocaleString('en-US');
    const hasNoProducts = !isLoading && productCount === 0;

    const getSyncText = () => {
        if (!csv && method === METHODS.FAILED_PROGRESS) {
            return 'Products Not Imported';
        }

        if (!csv && method === METHODS.FAILED_BUT_HAS_PRODUCTS) {
            return 'Not All Products Imported';
        }

        if (hasNoProducts && googleFeed) {
            return 'Syncing your catalog from Google Merchant Center...';
        }
        if (hasNoProducts && metaFeed) {
            return 'Syncing your catalog from Meta...';
        }

        if (isLoading) {
            return `products synced via ${syncMethod}`;
        }
        return `${formattedCount} products synced via ${syncMethod}`;
    };

    const updatedAt = productSetupSettings?.updatedAt;
    const formattedDate = updatedAt ? dayjs(updatedAt).format('D MMMM YYYY') : dayjs().format('D MMMM YYYY');

    const handleUpdateProducts = () => {
        navigate(ROUTE_PATHS.PRODUCTS_IMPORT);
    };

    const handleContactSupport = () => {
        setIsModalOpen(true);
    };

    return (
        <StyledBanner data-e2e='products_sync-banner' style={BOTTOM_INDENTS.XL_PLUS}>
            <StyledIconWrapper>
                <Icon.EntireCatalogIcon style={{ width: '80px', height: '80px', color: COLORS.$gray40Black }} />
            </StyledIconWrapper>
            <Flex vertical gap={8} style={{ flex: 1 }}>
                <Spin spinning={isLoading} indicator={<Loader size='20px' />}>
                    <Typography.Text type='badgeLarge' color={COLORS.$gray100Black}>
                        {getSyncText()}
                    </Typography.Text>
                </Spin>

                {method === METHODS.SYNC_PROGRESS ? (
                    <Flex align='center' gap={8}>
                        <StyledBadgeNoColor>
                            <Loader size='24px' position='relative' />
                        </StyledBadgeNoColor>
                    </Flex>
                ) : (
                    <Flex align='center' gap={8}>
                        {[METHODS.FAILED_PROGRESS, METHODS.FAILED_BUT_HAS_PRODUCTS].includes(method) ? (
                            <StyledBadgeError>
                                <Icon.CloseIcon style={{ width: '24px', height: '24px', color: COLORS.$white }} />
                            </StyledBadgeError>
                        ) : (
                            <StyledBadge>
                                <Icon.CheckIcon style={{ width: '24px', height: '24px', color: COLORS.$white }} />
                            </StyledBadge>
                        )}
                        <Typography.Text type='descriptor1' color={COLORS.$gray100Black}>
                            Last update {formattedDate}
                        </Typography.Text>
                    </Flex>
                )}
            </Flex>
            {[METHODS.FAILED_PROGRESS, METHODS.FAILED_BUT_HAS_PRODUCTS].includes(method) && (
                <StyledButton onClick={handleContactSupport} data-e2e='products_contact-support_btn'>
                    <Flex align='center'>
                        <Icon.SupportIcon
                            style={{
                                marginRight: '8px',
                                width: '24px',
                                height: '24px',
                                color: COLORS.$gray60Black
                            }}
                        />
                        <Typography.Text type='badgeMedium' color={COLORS.$gray60Black}>
                            Contact Support
                        </Typography.Text>
                    </Flex>
                </StyledButton>
            )}
            {method === METHODS.ONLY_CSV && (
                <StyledButton onClick={handleUpdateProducts} data-e2e='products_update-products_btn'>
                    <Flex align='center'>
                        <Icon.DownloadIcon
                            style={{
                                marginRight: '8px',
                                width: '24px',
                                height: '24px',
                                color: COLORS.$gray60Black
                            }}
                        />
                        <Typography.Text type='badgeMedium' color={COLORS.$gray60Black}>
                            Update Products
                        </Typography.Text>
                    </Flex>
                </StyledButton>
            )}
            <SupportModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
        </StyledBanner>
    );
});

export default SyncBanner;
